import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { getUnitFamily, STRICT_UNITS, UNIT_ID_MAP } from '../utils/unitDisplay';

const SmartUnitDropdown = forwardRef(({ 
  currentUnit, 
  currentUnitId,
  onUnitChange,
  onCrossFamilyConversion,
  onOtherUnitsClick,
  disabled = false,
  className = '',
  style = {},
  isNewItem: isNewItemProp,
  hideOtherUnits = false,
  direction = 'up',
  borderless = false,
  minWidth = '160px',
}, ref) => {
  const [showAllUnits, setShowAllUnits] = useState(false);
  const [selectedUnit, setSelectedUnit] = useState(currentUnit || null);
  const [isOpen, setIsOpen] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState(null);
  
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);

  useImperativeHandle(ref, () => ({
    focus: () => {
      if (triggerRef.current) triggerRef.current.focus();
    },
    open: () => {
      if (!disabled) setIsOpen(true);
    }
  }), [disabled]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Determine current state
  const isNewItem = isNewItemProp !== undefined ? isNewItemProp : (!currentUnit && !currentUnitId); // State A
  const isChangingFamily = showAllUnits; // State C

  // Get available units based on state
  const getAvailableUnits = () => {
    if (isNewItem || isChangingFamily) {
      return [
        { id: 1, symbol: 'kg', label: 'Kilogram (kg)', family: 'WEIGHT' },
        { id: 2, symbol: 'g', label: 'Gram (g)', family: 'WEIGHT' },
        { id: 4, symbol: 'L', label: 'Liter (L)', family: 'VOLUME' },
        { id: 5, symbol: 'ml', label: 'Milliliter (ml)', family: 'VOLUME' },
        { id: 9, symbol: 'pcs', label: 'Pieces (pcs)', family: 'COUNT' }
      ];
    }

    const family = getUnitFamily(currentUnit || UNIT_ID_MAP[currentUnitId]);
    
    if (family === 'WEIGHT') {
      return [
        { id: 1, symbol: 'kg', label: 'Kilogram (kg)', family: 'WEIGHT' },
        { id: 2, symbol: 'g', label: 'Gram (g)', family: 'WEIGHT' }
      ];
    } else if (family === 'VOLUME') {
      return [
        { id: 4, symbol: 'L', label: 'Liter (L)', family: 'VOLUME' },
        { id: 5, symbol: 'ml', label: 'Milliliter (ml)', family: 'VOLUME' }
      ];
    } else if (family === 'COUNT') {
      return [
        { id: 9, symbol: 'pcs', label: 'Pieces (pcs)', family: 'COUNT' }
      ];
    }

    return [
      { id: 1, symbol: 'kg', label: 'Kilogram (kg)', family: 'WEIGHT' },
      { id: 2, symbol: 'g', label: 'Gram (g)', family: 'WEIGHT' },
      { id: 4, symbol: 'L', label: 'Liter (L)', family: 'VOLUME' },
      { id: 5, symbol: 'ml', label: 'Milliliter (ml)', family: 'VOLUME' },
      { id: 9, symbol: 'pcs', label: 'Pieces (pcs)', family: 'COUNT' }
    ];
  };

  const handleUnitSelection = (unitId, unitSymbol) => {
    const currentFamily = getUnitFamily(currentUnit || UNIT_ID_MAP[currentUnitId]);
    const newFamily = getUnitFamily(unitSymbol);

    if (currentFamily && newFamily && currentFamily !== newFamily) {
      if (onCrossFamilyConversion) {
        onCrossFamilyConversion({
          currentUnit: currentUnit || UNIT_ID_MAP[currentUnitId],
          newUnit: unitSymbol,
          newUnitId: unitId
        });
      }
    } else {
      setSelectedUnit(unitSymbol);
      if (onUnitChange) {
        onUnitChange(unitId, unitSymbol);
      }
    }

    setShowAllUnits(false);
    setIsOpen(false);
  };

  const handleChangeFamilyClick = (e) => {
    e.stopPropagation();
    setShowAllUnits(true);
  };

  const availableUnits = getAvailableUnits();
  const showChangeFamilyOption = !isNewItem && !isChangingFamily;

  useEffect(() => {
    if (currentUnit) {
      setSelectedUnit(currentUnit);
    } else if (currentUnitId) {
      setSelectedUnit(UNIT_ID_MAP[currentUnitId]);
    }
  }, [currentUnit, currentUnitId]);

  const activeSymbol = selectedUnit || 'g';

  return (
    <div 
      ref={dropdownRef}
      className={`smart-unit-dropdown ${className}`} 
      style={{ 
        position: 'relative', 
        display: 'inline-block',
        ...style 
      }}
    >
      {/* Custom dropdown trigger */}
      <div
        ref={triggerRef}
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={(e) => {
          if (disabled) return;
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen((prev) => !prev);
          }
          if (e.key === 'Escape') {
            setIsOpen(false);
          }
        }}
        style={{
          height: '30px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: borderless ? 'center' : 'space-between',
          border: borderless ? 'none' : '1px solid #d1d5db',
          borderRadius: borderless ? '0' : '8px',
          background: borderless ? 'transparent' : (disabled ? '#f5f5f5' : '#fff'),
          padding: borderless ? '0' : '0 10px',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          boxSizing: 'border-box',
          width: '100%'
        }}
      >
        <span style={{ 
          fontSize: '15px', 
          color: '#000000',
          fontWeight: borderless ? 'normal' : 'bold',
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap' 
        }}>
          {activeSymbol}
        </span>
        {!borderless && (
          <span style={{
            border: 'solid #000000',
            borderWidth: '0 2px 2px 0',
            display: 'inline-block',
            padding: '2px',
            transform: isOpen ? 'rotate(-135deg)' : 'rotate(45deg)',
            transition: 'transform 0.15s ease',
            marginLeft: '4px',
            flexShrink: 0
          }} />
        )}
      </div>

      {/* Custom options dropdown list */}
      {isOpen && !disabled && (
        <div
          style={{
            position: 'absolute',
            ...(direction === 'down' 
              ? { top: 'calc(100% + 4px)' } 
              : { bottom: 'calc(100% + 4px)' }),
            right: 0,
            zIndex: 5000,
            background: '#ffffff',
            borderRadius: '8px',
            boxShadow: direction === 'down'
              ? '0 4px 12px rgba(0,0,0,0.15)'
              : '0 -4px 12px rgba(0,0,0,0.15)',
            border: '1px solid #e5e7eb',
            padding: '6px',
            minWidth: minWidth,
            width: 'max-content',
            maxWidth: '90vw',
            boxSizing: 'border-box',
            maxHeight: '150px',
            overflowY: 'auto',
            overflowX: 'hidden'
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {availableUnits.map((unit, idx) => {
              const isSelected = unit.symbol === activeSymbol;
              const isHovered = hoveredIndex === idx;
              return (
                <div
                  key={unit.id}
                  onClick={() => handleUnitSelection(unit.id, unit.symbol)}
                  onMouseEnter={() => setHoveredIndex(idx)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  style={{
                    padding: '8px 10px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: borderless ? 'normal' : 'bold',
                    background: isSelected 
                      ? '#f3f4f6' 
                      : (isHovered ? '#f9fafb' : 'transparent'),
                    color: '#000000',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    whiteSpace: 'nowrap',
                    userSelect: 'none'
                  }}
                >
                  <span>{unit.symbol}</span>
                </div>
              );
            })}

            {showChangeFamilyOption && !hideOtherUnits && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                  if (onOtherUnitsClick) {
                    onOtherUnitsClick();
                  } else {
                    handleChangeFamilyClick(e);
                  }
                }}
                onMouseEnter={() => setHoveredIndex('other-units')}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  padding: '8px 10px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '12px',
                  fontWeight: '600',
                  background: hoveredIndex === 'other-units' ? '#f3f4f6' : '#ffffff',
                  color: '#000000',
                  textAlign: 'center',
                  marginTop: '4px',
                  border: '1px dashed #d1d5db',
                  userSelect: 'none'
                }}
              >
                + Other Units
              </div>
            )}

            {isChangingFamily && (
              <div
                onClick={(e) => {
                  e.stopPropagation();
                  setShowAllUnits(false);
                }}
                onMouseEnter={() => setHoveredIndex('back')}
                onMouseLeave={() => setHoveredIndex(null)}
                style={{
                  padding: '6px 10px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: hoveredIndex === 'back' ? '#f3f4f6' : '#f9fafb',
                  color: '#4b5563',
                  textAlign: 'center',
                  marginTop: '4px',
                  border: '1px solid #e5e7eb',
                  userSelect: 'none'
                }}
              >
                ← Back to Same Family
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default SmartUnitDropdown;
